import dayjs from "dayjs";
import { getAuth } from "firebase/auth";
import { getDatabase, onValue, ref, remove, set } from "firebase/database";
import { useFirebase } from "../composables/useFirebase";

/*
    check-in ของเครื่อง — หลักฐานว่าเครื่องเปิดอยู่จริงช่วงไหนบ้าง

    ทำไมต้องมี : ร้านยืนยันว่าเครื่อง Main Cashier เปิดตลอด แต่ support ไม่มีอะไรไว้เช็ค
    node online/{computerId} (shop.ts) ถูกลบตอนหลุด เลยบอกได้แค่ "ตอนนี้" ไม่เหลือประวัติ

    ทุกเครื่องที่ login อยู่ (ทุกโหมด รวมเครื่องที่ยังไม่ได้ตั้งโหมด) เขียนทุกนาที :
      stores/{storeId}/checkin/{computerId}/{YYYY-MM-DD::HH}/{mm} = true
      เช่น stores/4012/checkin/ab12cd/2026-10-11::14/05 = true
    เวลาเป็นเวลาท้องถิ่นของเครื่อง (ไม่ใช่ server time) ดูใน Firebase console ตรง ๆ ได้เลย
      ขาดไปไม่กี่นาที  -> เน็ตหลุดชั่วคราว (ปกติ)
      ขาดเป็นชั่วโมง   -> เครื่องปิดอยู่จริง

    ไม่มีส่วนไหนของแอปอ่าน node นี้ — support อ่านเองจาก console
    Main Cashier ล้างทั้ง node ทุกครั้งที่ปิดร้าน (close.vue) ข้อมูลจึงอยู่ไม่เกิน 1 รอบขาย

    เขียนด้วย set() ตรง ๆ ไม่ผ่าน offline queue (useOffline) และข้ามรอบที่ RTDB ไม่ได้ต่ออยู่
    ไม่งั้น SDK จะเก็บ write ค้างไว้แล้วยิงทีเดียวตอนต่อติด ช่วงที่หลุดจะดูเหมือนออนไลน์
*/

// เผื่อ timer ยิงก่อนขึ้นนาทีใหม่นิดเดียว (drift) จะได้ไม่เขียนนาทีเดิมซ้ำแล้วข้ามนาทีถัดไป
const TICK_OFFSET_MS = 1000;
// ปิดร้านตอน offline remove() จะไม่ resolve จนกว่าจะต่อเน็ตได้ — ไม่ให้ขั้นปิดร้านค้าง
const CLEAR_TIMEOUT_MS = 5000;
// key เดียวกับ modules/seat/utils/seatStorage.ts (seatBlock เป็นคนเขียน)
const DEVICE_BLOCKED_KEY = "deviceBlocked";

interface CheckinState {
  storeId: string;
  getComputerId: () => string | null | undefined;
  timer: ReturnType<typeof setTimeout> | null;
  unsubConnected: (() => void) | null;
  connected: boolean;
}

// เก็บ handle ไว้บน window ไม่ใช่ตัวแปร module — HMR โหลด module ใหม่แล้ว timer เก่า
// ยังเดินอยู่ ถ้าเก็บใน module จะหยุดตัวเก่าไม่ได้และได้ timer ซ้อนกัน
const GLOBAL_KEY = "__xtableCheckin";

function getState(): CheckinState | null {
  return (globalThis as any)[GLOBAL_KEY] || null;
}

function setState(state: CheckinState | null) {
  (globalThis as any)[GLOBAL_KEY] = state;
}

/** path ของ check-in นาทีนี้ — แยกออกมาไว้ใช้ทดสอบ / อ้างอิงใน PR */
export function checkinPath(storeId: string, computerId: string, now = dayjs()): string {
  return `stores/${storeId}/checkin/${computerId}/${now.format("YYYY-MM-DD::HH")}/${now.format("mm")}`;
}

function isDeviceBlocked(): boolean {
  try {
    return !!localStorage.getItem(DEVICE_BLOCKED_KEY);
  } catch {
    return false;
  }
}

function writeCheckin(state: CheckinState) {
  if (isDeviceBlocked() || !getAuth().currentUser) {
    stopCheckin();
    return;
  }
  if (!state.connected) return;

  const computerId = state.getComputerId();
  if (!computerId) return;

  set(ref(getDatabase(), checkinPath(state.storeId, computerId)), true).catch((e) =>
    console.warn("[checkin] เขียน check-in ไม่สำเร็จ", e)
  );
}

// ตั้งรอบถัดไปให้ตรงขอบนาทีทุกครั้ง (ไม่ใช้ setInterval ที่ drift สะสมจนข้ามนาที)
function scheduleNext(state: CheckinState) {
  const delay = 60_000 - (Date.now() % 60_000) + TICK_OFFSET_MS;
  state.timer = setTimeout(() => {
    if (getState() !== state) return;
    writeCheckin(state);
    if (getState() === state) scheduleNext(state);
  }, delay);
}

/**
 * เริ่มเขียน check-in ของเครื่องนี้ทุกนาที — เรียกซ้ำได้ (ร้านเดิม = ไม่ทำอะไร)
 * getComputerId อ่านสดทุกรอบ เผื่อเปลี่ยน ID เครื่องจากหน้า settings กลาง session
 */
export function startCheckin(storeId: string, getComputerId: () => string | null | undefined) {
  if (!storeId || !getComputerId()) return;
  if (isDeviceBlocked() || !getAuth().currentUser) return;

  const current = getState();
  if (current && current.storeId === storeId) {
    current.getComputerId = getComputerId;
    return;
  }
  stopCheckin();

  const state: CheckinState = {
    storeId,
    getComputerId,
    timer: null,
    unsubConnected: null,
    connected: false,
  };
  setState(state);

  // ต่อติดเมื่อไหร่เขียนทันที 1 ครั้ง (ครอบคลุมการเขียนครั้งแรกตอนเริ่ม และตอนต่อเน็ตกลับมา)
  state.unsubConnected = onValue(ref(getDatabase(), ".info/connected"), (snap) => {
    state.connected = snap.val() === true;
    if (state.connected && getState() === state) writeCheckin(state);
  });

  scheduleNext(state);
}

/** หยุด timer / listener — ไม่ลบข้อมูลที่เขียนไปแล้ว (logout ก็ยังต้องเห็นประวัติ) */
export function stopCheckin() {
  const state = getState();
  if (!state) return;
  setState(null);
  if (state.timer) clearTimeout(state.timer);
  state.unsubConnected?.();
}

/**
 * ล้าง check-in ทั้งร้าน — Main Cashier เรียกตอนปิดร้าน
 * ห้าม throw: ลบไม่ได้แค่ข้อมูลเก่าค้างอีกวัน ไม่ใช่เหตุให้ปิดร้านไม่ผ่าน
 */
export async function clearCheckin(storeId: string | null = useFirebase().getStoreId()) {
  if (!storeId) return;

  try {
    await Promise.race([
      remove(ref(getDatabase(), `stores/${storeId}/checkin`)),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("timeout")), CLEAR_TIMEOUT_MS)
      ),
    ]);
  } catch (e) {
    console.warn("[checkin] ล้าง check-in ไม่สำเร็จ", e);
  }
}
